import React from 'react';
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  Android,
  Email,
  HelpCenter,
  LocationOn,
  WhatsApp,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

const SUPPORT_EMAIL = 'info@kundlistarstudio.com';
const INFO_EMAIL = 'info@kundlistarstudio.com';
const PHONE_NUMBER = '+91 8284927820';
const WHATSAPP_URL = 'https://wa.me/918284927820';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.kundlistar.app';

const contactMethods = [
  {
    title: 'Email Support',
    detail: SUPPORT_EMAIL,
    helper: 'For account, subscription, report, privacy, and technical support.',
    href: `mailto:${SUPPORT_EMAIL}`,
    icon: <Email />,
    action: 'Send Email',
  },
  {
    title: 'Business & Partnerships',
    detail: INFO_EMAIL,
    helper: 'For collaborations, product queries, and Kundlistar business enquiries.',
    href: `mailto:${INFO_EMAIL}`,
    icon: <HelpCenter />,
    action: 'Contact Team',
  },
  {
    title: 'WhatsApp',
    detail: PHONE_NUMBER,
    helper: 'For quick help, app guidance, and astrology consultation enquiries.',
    href: WHATSAPP_URL,
    icon: <WhatsApp />,
    action: 'Open WhatsApp',
  },
];

const supportTopics = [
  'Android app installs',
  'plan access at Rs 999/year',
  'Saved kundli and reports',
  'AI astrology reports',
  'Account deletion and privacy',
  'Technical support',
];

const Contact: React.FC = () => {
  return (
    <Box sx={{ bgcolor: '#f7faf8', minHeight: '100vh' }}>
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          pt: { xs: 6, md: 9 },
          pb: { xs: 7, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 7 }}>
              <Chip
                label="Kundlistar Support"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.16)',
                  color: 'white',
                  fontWeight: 600,
                  mb: 2,
                }}
              />
              <Typography variant="h2" component="h1" fontWeight={600} gutterBottom sx={{ color: 'white' }}>
                Contact Kundlistar
              </Typography>
              <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.7, maxWidth: 760 }}>
                Need help with kundli, premium reports, Android app access, subscriptions, or account support?
                Reach the Kundlistar team through email, WhatsApp, or phone.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  startIcon={<Email />}
                  component="a"
                  href={`mailto:${SUPPORT_EMAIL}`}
                >
                  Email Support
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<WhatsApp />}
                  component="a"
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.75)' }}
                >
                  WhatsApp
                </Button>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 5 }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 2,
                  bgcolor: 'rgba(255,255,255,0.96)',
                }}
              >
                <Stack spacing={2.5}>
                  <Box>
                    <Typography variant="overline" color="text.secondary" fontWeight={600}>
                      Primary Email
                    </Typography>
                    <Typography variant="h6">
                      <Link href={`mailto:${SUPPORT_EMAIL}`} underline="hover">
                        {SUPPORT_EMAIL}
                      </Link>
                    </Typography>
                  </Box>
                  <Divider />
                  <Box>
                    <Typography variant="overline" color="text.secondary" fontWeight={600}>
                      Phone / WhatsApp
                    </Typography>
                    <Typography variant="h6">
                      <Link href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" underline="hover">
                        {PHONE_NUMBER}
                      </Link>
                    </Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <LocationOn color="primary" />
                    <Typography variant="body1">India</Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
        <Grid container spacing={3}>
          {contactMethods.map((method) => (
            <Grid key={method.title} size={{ xs: 12, md: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  height: '100%',
                  p: 3,
                  borderRadius: 2,
                  border: '1px solid rgba(46, 125, 82, 0.14)',
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 1.5,
                    bgcolor: 'rgba(46, 125, 82, 0.1)',
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                  }}
                >
                  {method.icon}
                </Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {method.title}
                </Typography>
                <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
                  {method.detail}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, mb: 2 }}>
                  {method.helper}
                </Typography>
                <Button
                  component="a"
                  href={method.href}
                  target={method.href.startsWith('http') ? '_blank' : undefined}
                  rel={method.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                  variant="outlined"
                  size="small"
                >
                  {method.action}
                </Button>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={4} sx={{ mt: 3 }} alignItems="stretch">
          <Grid size={{ xs: 12, md: 7 }}>
            <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: 2, height: '100%' }}>
              <Typography variant="h4" component="h2" fontWeight={600} gutterBottom>
                How We Can Help
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mb: 3 }}>
                Contact us for help with Kundlistar features including kundli generation, matching,
                Panchang, Nadi life events, KP, BNN, Jaimini, Tajika Varshphal, Lal Kitaab,
                Chakra insights, numerology, Astro Vastu, and premium AI-based astrology reports.
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {supportTopics.map((topic) => (
                  <Chip key={topic} label={topic} variant="outlined" color="primary" />
                ))}
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8, mt: 3 }}>
                Astrology content on Kundlistar is offered for guidance, learning, and spiritual
                perspective. It should not replace professional medical, legal, financial, or
                mental health advice.
              </Typography>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 5 }}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 2,
                height: '100%',
                bgcolor: '#10261a',
                color: 'white',
              }}
            >
              <Android sx={{ fontSize: 42, color: 'secondary.main', mb: 2 }} />
              <Typography variant="h4" component="h2" fontWeight={600} gutterBottom sx={{ color: 'white' }}>
                Get Kundlistar On Android
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.84)', lineHeight: 1.8, mb: 3 }}>
                Download Kundlistar App now to explore your kundli, save charts, and unlock premium
                astrology reports at the limited launch offer for early users.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button
                  component="a"
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  color="secondary"
                  startIcon={<Android />}
                >
                  Download App
                </Button>
                <Button
                  component={RouterLink}
                  to="/pricing"
                  variant="outlined"
                  sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.65)' }}
                >
                  View Plans
                </Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Contact;
